/* =========================================================
   OVERTOP / Urban-Haul — Cart Quantity Cap
   ---------------------------------------------------------
   Hard store-wide limit: no order may contain more than MAX
   items total. Enforced at window.fetch — the single chokepoint
   every add path (product form, sticky bar, quick-add) and every
   quantity change flows through.

   The decision is made against a CACHED cart count (kept current
   from cart:update + a background refresh), so /cart/add never
   waits on a blocking pre-fetch — the cart drawer opens with no
   added latency. Optimistic in-flight accounting keeps the cap
   robust even under rapid clicks.

   - /cart/add   : clamp the requested quantity to the space left;
                   if the cart is already full, return a Shopify-style
                   422 so Horizon shows its native "can't add" error.
   - /cart/change: clamp a line's new quantity so the total can't
                   exceed MAX (uses the cached cart, no pre-fetch).
   ========================================================= */

(function () {
  'use strict';

  var MAX = 3;
  window.__OT_MAX_CART_ITEMS = MAX;
  window.__OT_QTY_CAP_VERSION = '1.1.0';

  var origFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!origFetch) return;

  function root() {
    try {
      return String((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, '');
    } catch (e) { return ''; }
  }
  function asUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }
  function isAdd(u) { return /\/cart\/add(\.js)?($|\?)/.test(u); }
  function isChange(u) { return /\/cart\/change(\.js)?($|\?)/.test(u); }
  function plural(n) { return n === 1 ? '' : 's'; }

  /* ---------- cached cart state (no blocking pre-fetch on add) ---------- */
  var cachedCart = null;
  var cachedCount = 0;
  var pending = 0;      // optimistic in-flight added quantity
  var inFlight = 0;     // number of add requests still in flight
  var refreshTimer = null;

  function applyCart(cart) {
    if (cart && typeof cart.item_count !== 'undefined') {
      cachedCart = cart;
      cachedCount = Math.max(0, Number(cart.item_count) || 0);
    }
    if (inFlight === 0) pending = 0; // only clear optimism once nothing is in flight
  }
  function fetchCount() {
    return origFetch(root() + '/cart.js', {
      headers: { 'Accept': 'application/json' }, credentials: 'same-origin', cache: 'no-store'
    }).then(function (r) { return r.json(); })
      .then(function (cart) { applyCart(cart); return cart; })
      .catch(function () { return null; });
  }
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(fetchCount, 60);
  }
  function effCount() { return cachedCount + pending; }

  document.addEventListener('cart:update', function (e) {
    var d = e && e.detail;
    var cart = d && (d.cart || (d.data && d.data.cart) || d.resource);
    if (cart && typeof cart.item_count !== 'undefined') applyCart(cart);
    else scheduleRefresh();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fetchCount);
  else fetchCount();

  /* ---------- toast ---------- */
  var toastStyled = false;
  function styleToast() {
    if (toastStyled) return;
    toastStyled = true;
    var css =
      '#otQtyCapToast{position:fixed;left:50%;bottom:calc(20px + env(safe-area-inset-bottom,0px));' +
      'transform:translateX(-50%) translateY(14px);z-index:2147483600;max-width:88vw;' +
      'background:#1c1d1d;color:#fff;font-weight:700;font-size:13.5px;letter-spacing:.2px;' +
      'padding:11px 16px;border-radius:12px;box-shadow:0 12px 30px rgba(0,0,0,.3);' +
      'opacity:0;visibility:hidden;transition:opacity .2s ease,transform .2s ease,visibility .2s;}' +
      '#otQtyCapToast.show{opacity:1;visibility:visible;transform:translateX(-50%) translateY(0);}';
    var s = document.createElement('style');
    s.id = 'ot-qty-cap-styles';
    s.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(s);
  }
  function toast(msg) {
    try { document.dispatchEvent(new CustomEvent('ot:qty-cap', { detail: { message: msg, max: MAX } })); } catch (e) {}
    if (!document.body) return;
    styleToast();
    var t = document.getElementById('otQtyCapToast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'otQtyCapToast';
      t.setAttribute('role', 'status');
      t.setAttribute('aria-live', 'polite');
      document.body.appendChild(t);
    }
    t.textContent = msg;
    requestAnimationFrame(function () { t.classList.add('show'); });
    clearTimeout(t.__otHide);
    t.__otHide = setTimeout(function () { t.classList.remove('show'); }, 3400);
  }

  function block422() {
    var payload = {
      status: 422,
      message: 'Maximum ' + MAX + ' items per order',
      description: 'You can order a maximum of ' + MAX + ' items per order.'
    };
    return new Response(JSON.stringify(payload), {
      status: 422, statusText: 'Unprocessable Entity',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  /* ---------- clamp helpers (return how much will actually be added) ---------- */
  function clampFormData(fd, remaining) {
    var q = 1;
    if (fd.has('quantity')) { q = parseInt(fd.get('quantity'), 10); if (isNaN(q)) q = 1; }
    var allow = Math.min(q, remaining);
    if (allow < q) fd.set('quantity', String(allow));
    return { added: allow, changed: allow < q };
  }
  function clampJson(data, remaining) {
    var added = 0, changed = false;
    if (data && Array.isArray(data.items)) {
      var budget = remaining;
      data.items.forEach(function (it) {
        var q = Number(it.quantity != null ? it.quantity : 1);
        var allow = Math.min(q, Math.max(0, budget));
        if (allow !== q) { it.quantity = allow; changed = true; }
        budget -= allow; added += allow;
      });
      var before = data.items.length;
      data.items = data.items.filter(function (it) { return Number(it.quantity || 0) > 0; });
      if (data.items.length !== before) changed = true;
    } else if (data && data.quantity != null) {
      var q2 = Number(data.quantity);
      var allow2 = Math.min(q2, remaining);
      if (allow2 !== q2) { data.quantity = allow2; changed = true; }
      added = allow2;
    } else {
      added = 1; // default quantity
    }
    return { added: added, changed: changed };
  }

  /* ---------- add (synchronous decision, no pre-fetch) ---------- */
  function fire(input, init, added) {
    pending += Math.max(0, added);
    inFlight++;
    var p = origFetch(input, init);
    var done = function () { inFlight = Math.max(0, inFlight - 1); scheduleRefresh(); };
    p.then(done, done);
    return p;
  }

  function handleAdd(input, init) {
    var remaining = MAX - effCount();
    if (remaining <= 0) {
      toast('Your cart is full — maximum ' + MAX + ' items per order.');
      return Promise.resolve(block422());
    }

    var body = init && init.body;

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var r1 = clampFormData(body, remaining);
      if (r1.changed) toast('Only ' + remaining + ' more item' + plural(remaining) + ' allowed (max ' + MAX + ').');
      return fire(input, init, r1.added); // FormData mutated in place
    }

    if (typeof body === 'string') {
      try {
        var data = JSON.parse(body);
        var r2 = clampJson(data, remaining);
        if (r2.changed) {
          toast('Only ' + remaining + ' more item' + plural(remaining) + ' allowed (max ' + MAX + ').');
          var ninit = {};
          for (var k in init) ninit[k] = init[k];
          ninit.body = JSON.stringify(data);
          return fire(input, ninit, r2.added);
        }
        return fire(input, init, r2.added);
      } catch (e) {}
    }

    return fire(input, init, 1);
  }

  /* ---------- change (clamp against the cached cart) ---------- */
  function passChange(input, init) {
    var p = origFetch(input, init);
    p.then(scheduleRefresh, scheduleRefresh);
    return p;
  }
  function clampAndSendChange(input, init, data, cart) {
    if (cart && cart.items) {
      var items = cart.items, count = Number(cart.item_count || 0), thisQty = 0;
      if (data.line != null) {
        var it = items[Number(data.line) - 1];
        thisQty = it ? Number(it.quantity || 0) : 0;
      } else if (data.id != null) {
        items.forEach(function (i) {
          if (String(i.key) === String(data.id) ||
              String(i.variant_id) === String(data.id) ||
              String(i.id) === String(data.id)) thisQty = Number(i.quantity || 0);
        });
      }
      var maxThis = Math.max(0, MAX - (count - thisQty));
      if (Number(data.quantity) > maxThis) {
        data.quantity = maxThis;
        toast('Maximum ' + MAX + ' items per order.');
        var ninit = {};
        for (var k in init) ninit[k] = init[k];
        ninit.body = JSON.stringify(data);
        return passChange(input, ninit);
      }
    }
    return passChange(input, init);
  }
  function handleChange(input, init) {
    var body = init && init.body;
    if (typeof body !== 'string') return passChange(input, init);
    var data;
    try { data = JSON.parse(body); } catch (e) { return passChange(input, init); }
    if (data.quantity == null) return passChange(input, init);

    if (cachedCart) return clampAndSendChange(input, init, data, cachedCart);
    // No cache yet (rare) — fetch once, then decide.
    return fetchCount().then(function () { return clampAndSendChange(input, init, data, cachedCart); });
  }

  /* ---------- wrap fetch ---------- */
  window.fetch = function (input, init) {
    try {
      var u = asUrl(input);
      if (u) {
        if (isAdd(u)) return handleAdd(input, init || {});
        if (isChange(u)) return handleChange(input, init || {});
      }
    } catch (e) {}
    return origFetch(input, init);
  };
})();
